/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler, useCallback} from 'react';
import {makeStyles} from '@material-ui/core';
import {IconButton} from '../../components/buttons/IconButton';
import {PauseIcon} from '../../icons/PauseIcon';
import {PlayIcon} from '../../icons/PlayIcon';
import {ReplayIcon} from '../../icons/ReplayIcon';
import {IconSize} from '../../types/types';

export interface PlayPauseProps {
  playing: boolean;
  restart?: boolean;
  size?: IconSize | number;
  handlePlayPause: MouseEventHandler<HTMLButtonElement>;
  isCondensedVariant?: boolean;
}

const useStyles = makeStyles(theme => ({
  playPauseWrapper : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    width          : '50px',
    height         : '50px'
  },
  condensedWrapper : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center'
  }
}));

/**
 * @PlayPause
 *
 * Defines the Customized Play-Pause Component. It is for playing and pausing the react video player
 *
 * ```typescript
 *  import PlayPause from '@ion/dls/src/components/videoplayer/PlayPause';
 *
 *  const [playing, setPlaying] = useState(false);
 *  function handlePlayPause() {
 *   setPlaying(!playing);
 *  }
 *
 *  <PlayPause playing={playing}
 *     handlePlayPause={handlePlayPause} />
 *
 * ```
*/
const PlayPause = ({
  playing,
  restart,
  size = 24,
  handlePlayPause,
  isCondensedVariant
}: PlayPauseProps) => {
  const classes = useStyles(playing);

  const getIconType = useCallback(function() {
    if (restart) {
      return <ReplayIcon size={size}/>;
    }
    if (playing) {
      return <PauseIcon size={size} />;
    }
    return <PlayIcon size={size} />;
  }, [playing, restart, size]);

  return (
    <div className={isCondensedVariant ? classes.condensedWrapper: classes.playPauseWrapper}>
      <IconButton onClick={handlePlayPause}
        icon={getIconType()}
        padding={isCondensedVariant ? '0px' : '8px'} />
    </div>
  );
};

export default PlayPause;
